import React, { useMemo, useState } from "react";
import { Trophy, ExternalLink, ShieldCheck, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";

const RELIABILITY = {
  Verified: {
    label: "Verified",
    icon: ShieldCheck,
    classes: "bg-[#4ADE80]/10 text-[#4ADE80] border-[#4ADE80]/30",
  },
  "Self-reported": {
    label: "Self-reported",
    icon: MessageSquare,
    classes: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  },
};

const DEFAULT_VISIBLE = 3;

export default function EarningsList({ earnings = [] }) {
  // Keep only entries with a figure + URL
  const valid = useMemo(
    () =>
      (earnings || []).filter(
        (e) => e && (e.reported_figure || "").trim() && (e.source_url || "").trim(),
      ),
    [earnings],
  );

  // Sort: Verified (tier 1) first — most solo-operator-relevant proof up top
  const sorted = useMemo(() => {
    return [...valid].sort((a, b) => {
      const aTier = a.tier === 1 || a.reliability === "Verified" ? 0 : 1;
      const bTier = b.tier === 1 || b.reliability === "Verified" ? 0 : 1;
      return aTier - bTier;
    });
  }, [valid]);

  const [expanded, setExpanded] = useState(false);

  if (sorted.length === 0) {
    return (
      <div className="gl-card text-sm text-[#b6c1b8]" data-testid="earnings-empty">
        We could not find any attributed earnings figures for this specific idea
        in public sources. Treat any revenue estimate as a guess.
      </div>
    );
  }

  const hasMore = sorted.length > DEFAULT_VISIBLE;
  const visible = expanded ? sorted : sorted.slice(0, DEFAULT_VISIBLE);

  return (
    <div data-testid="earnings-list">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {visible.map((e, i) => {
          const rel = e.reliability || (e.tier === 1 ? "Verified" : "Self-reported");
          const variant = RELIABILITY[rel] || RELIABILITY["Self-reported"];
          const Icon = variant.icon;
          return (
            <div
              key={e.source_url || `earnings-${i}`}
              className="gl-card-lift p-4"
              data-testid={`earnings-${i}`}
            >
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Trophy size={13} className="text-[#4ADE80]" />
                <span className="gl-label text-[10px]">Reported Earnings</span>
                <span
                  className={`ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${variant.classes}`}
                >
                  <Icon size={11} strokeWidth={2.4} />
                  {variant.label}
                </span>
              </div>
              <div className="text-[20px] sm:text-2xl font-bold text-white mb-1 leading-tight">
                {e.reported_figure}
              </div>
              <div className="text-[13px] sm:text-sm text-[#b6c1b8] leading-relaxed mb-3">
                {e.context}
              </div>
              <a
                href={e.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-[#4ADE80] hover:underline truncate"
              >
                <span className="truncate max-w-[220px]">{e.source_url}</span>
                <ExternalLink size={12} />
              </a>
            </div>
          );
        })}
      </div>

      {hasMore ? (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid="earnings-toggle"
            className="inline-flex items-center gap-2 text-sm text-[#4ADE80] font-semibold border border-[#4ADE80]/30 bg-[#4ADE80]/5 hover:bg-[#4ADE80]/10 rounded-full px-4 py-2 transition-colors"
          >
            {expanded ? (
              <>
                <ChevronUp size={14} />
                Show fewer
              </>
            ) : (
              <>
                <ChevronDown size={14} />
                Show all earnings ({sorted.length})
              </>
            )}
          </button>
        </div>
      ) : null}
    </div>
  );
}
